import * as v from 'valibot';

import { ENOPOrganType } from '#shared';

import { ENopConsoleStatus } from '../../model/types';

// Response envelopes for the NOP resources boundary (BE ADR CODE-012).
//
// These validate the NORMALISED shape `asResponseResult` produces, not the wire
// shape — `/api/nop/*` answers `{ status, cached, data, meta }` and the api
// adapts it before validating, so one validation mechanism covers every endpoint
// in the repo.
//
// Severity choices follow DATA-001. A field the dashboard renders directly is an
// ERROR, which drops the item: a console with no organ has no colour and no
// place in the grid, so half-rendering it is worse than omitting it. Everything
// the screen degrades gracefully without is a WARNING.

/**
 * `Common.Queries.LocationView`.
 *
 * `timezone` is nullable upstream and NOT defaulted here. A hub whose zone is
 * missing must reach the dashboard as missing so the clock can say so, rather
 * than silently rendering the viewer's own zone as if it were the hub's.
 */
const nopLocationSchema = v.object({
  id: v.string('NopLocation.id'),
  country: v.nullable(v.string()),
  regionId: v.nullable(v.string()),
  region: v.nullable(v.string()),
  state: v.nullable(v.string()),
  city: v.nullable(v.string()),
  timezone: v.nullable(v.string()),
  latitude: v.nullable(v.number()),
  longitude: v.nullable(v.number()),
});

const nopHubSchema = v.object({
  id: v.string('NopHub.id'),
  name: v.nullable(v.string()),
  code: v.nullable(v.string()),
  location: nopLocationSchema,
  isSuperHub: v.boolean('NopHub.isSuperHub'),
});

/**
 * A reservation on a console or an employee.
 *
 * `status` stays an open string rather than `ENOPResourceLogisticsStatus`: the
 * consumer only tests membership of a two-value set, and a new upstream status
 * must not drop the whole console for being unrecognised.
 */
const nopReservationSchema = v.object({
  caseId: v.string('NopReservation.caseId'),
  status: v.string('NopReservation.status'),
});

const nopConsoleSchema = v.object({
  id: v.string('NopConsole.id'),
  ocsConsoleName: v.nullable(v.string()),
  serialNumber: v.nullable(v.string()),
  fullSerialNumber: v.nullable(v.string()),
  nopHub: v.nullable(v.string()),
  organType: v.enum(ENOPOrganType, 'NopConsole.organType'),
  status: v.enum(ENopConsoleStatus, 'NopConsole.status'),
  /**
   * The field this whole boundary buys the device grid. Not optional and not
   * defaulted: the report hid it behind an `isInMaintenance=false` query, so
   * quietly falling back to `false` here would reproduce that bug in the FE.
   */
  isInMaintenance: v.boolean('NopConsole.isInMaintenance'),
  caseId: v.nullable(v.string()),
  caseName: v.nullable(v.string()),
  caseNumber: v.nullable(v.string()),
  reservations: v.nullable(v.array(nopReservationSchema)),
  // Opaque upstream, and only `name` is read. Loose on purpose so a nested
  // upstream change costs a missing trial tint rather than a dropped console.
  trialProgram: v.nullable(
    v.object({ name: v.optional(v.nullable(v.string())) }),
  ),
});

const nopCSShiftSchema = v.object({
  scheduleId: v.string('NopCSShift.scheduleId'),
  /**
   * The OWNING day, not the start. Required, because bucketing by `from`
   * silently misplaces every shift that crosses midnight.
   */
  day: v.string('NopCSShift.day'),
  from: v.string('NopCSShift.from'),
  to: v.string('NopCSShift.to'),
  resourceId: v.string('NopCSShift.resourceId'),
  /**
   * Open strings rather than enums, on purpose. NOP's ten shift types and three
   * statuses are both expected to grow (CODE-002 `Q-9`), and a new value must
   * render as an unrecognised band rather than drop the shift and leave a hole in
   * the ribbon.
   */
  shiftStatus: v.string('NopCSShift.shiftStatus'),
  shiftType: v.string('NopCSShift.shiftType'),
  organs: v.nullable(v.array(v.enum(ENOPOrganType))),
  regionId: v.nullable(v.string()),
  hubId: v.nullable(v.string()),
  isTravel: v.optional(v.boolean()),
  isExtra: v.optional(v.boolean()),
});

const nopCSResourceSchema = v.object({
  id: v.string('NopCSResource.id'),
  firstName: v.nullable(v.string()),
  lastName: v.nullable(v.string()),
  profilePhoto: v.nullable(v.string()),
  organs: v.nullable(v.array(v.enum(ENOPOrganType))),
  phoneSupportOrgans: v.nullable(v.array(v.enum(ENOPOrganType))),
  canPhoneSupport: v.boolean('NopCSResource.canPhoneSupport'),
  regionId: v.nullable(v.string()),
  hubId: v.nullable(v.string()),
});

/**
 * One date's shifts for one hub.
 *
 * `days` is nullable upstream — a resource can appear in a partition with no
 * shift on that day — so it must not be required here or the whole date drops.
 */
const nopShiftPartitionSchema = v.object({
  from: v.string('NopShiftPartition.from'),
  to: v.string('NopShiftPartition.to'),
  resourcesShifts: v.record(
    v.string(),
    v.object({
      resource: nopCSResourceSchema,
      days: v.nullable(v.record(v.string(), nopCSShiftSchema)),
    }),
  ),
});

/**
 * One entry per requested date, each carrying its own status.
 *
 * `data` is nullable per date rather than the date being omitted: `absent` (never
 * swept) and `empty` (swept, no shifts) are different answers and both have to
 * survive to the ruler.
 */
const nopShiftDaySchema = v.object({
  date: v.string('NopShiftDay.date'),
  status: v.picklist(['absent', 'empty', 'present']),
  data: v.nullable(nopShiftPartitionSchema),
});

/**
 * An employee, narrowed to the fields the dashboard reads.
 *
 * Everything optional-and-nullable except `id`: the payload has ~50 fields, most
 * of them absent on most records, and dropping an employee because one badge input
 * is missing would remove a person from the roster over a cosmetic flag.
 */
const nopEmployeeSchema = v.object({
  id: v.string('NopEmployee.id'),
  firstName: v.nullish(v.string()),
  lastName: v.nullish(v.string()),
  organSpecializations: v.nullish(v.array(v.enum(ENOPOrganType))),
  proctorOrganSpecializations: v.nullish(v.array(v.enum(ENOPOrganType))),
  shadowOrganSpecializations: v.nullish(v.array(v.enum(ENOPOrganType))),
  trialPrograms: v.nullish(
    v.array(
      v.object({
        id: v.string(),
        name: v.nullish(v.string()),
        organ: v.enum(ENOPOrganType),
        isActive: v.boolean(),
      }),
    ),
  ),
  reservations: v.nullish(
    v.array(
      v.object({
        caseId: v.string('NopReservation.caseId'),
        caseName: v.nullish(v.string()),
        status: v.string('NopReservation.status'),
        createdAt: v.nullish(v.string()),
      }),
    ),
  ),
  activeRestPeriod: v.nullish(
    v.object({
      restStartTime: v.string('NopRestPeriod.restStartTime'),
      restEndTime: v.string('NopRestPeriod.restEndTime'),
      actualRestEndTime: v.nullish(v.string()),
      restCancellationTime: v.nullish(v.string()),
      caseId: v.nullish(v.string()),
      caseName: v.nullish(v.string()),
    }),
  ),
});

/**
 * A past `OnCase` or `OnRest` node, flattened by `asEmployeeReservationNodes`.
 *
 * `from` is an ERROR: a node with no start has no place on the axis. `to`,
 * `caseId` and `caseName` are nullable upstream and stay nullable here.
 */
const nopEmployeeReservationNodeSchema = v.object({
  employeeId: v.string('NopEmployeeReservationNode.employeeId'),
  nodeId: v.string('NopEmployeeReservationNode.nodeId'),
  from: v.string('NopEmployeeReservationNode.from'),
  to: v.nullable(v.string()),
  type: v.string('NopEmployeeReservationNode.type'),
  caseId: v.nullable(v.string()),
  caseName: v.nullable(v.string()),
});

const responseEnvelope = <TSchema extends v.GenericSchema>(item: TSchema) =>
  v.object({
    data: v.array(item),
    dataGeneratedAt: v.nullable(v.string()),
    errors: v.nullable(v.array(v.unknown())),
    expires: v.nullable(v.string()),
    errorsGeneratedAt: v.nullable(v.string()),
  });

export const nopHubsResponseSchema = responseEnvelope(nopHubSchema);
export const nopConsolesResponseSchema = responseEnvelope(nopConsoleSchema);
export const nopShiftDaysResponseSchema = responseEnvelope(nopShiftDaySchema);
export const nopEmployeesResponseSchema = responseEnvelope(nopEmployeeSchema);
export const nopEmployeeReservationNodesResponseSchema = responseEnvelope(
  nopEmployeeReservationNodeSchema,
);

export type NopHubsResponse = v.InferInput<typeof nopHubsResponseSchema>;
export type NopConsolesResponse = v.InferInput<
  typeof nopConsolesResponseSchema
>;
